// TelcoTrack — RAY-OFSM module catalog for RSU / RAY-TR3 (42U rack).
// Users insert whole modules; trays / connector fields are derived from SAP.
// 325702 training + SAP name = 6×1:8 (v25 table 8×1:8 is not used).

import type { RozvadecSplitterRatio } from './rozvadec-cabinet.constants';

export const RAY_TR3_RACK_HEIGHT_U = 42;
export const RAY_OFSM_COLUMN_SIZE = 12;

export const RAY_OFSM_SAPS = [
  '325704',
  '325713',
  '325712',
  '326203',
  '325702',
  '325703',
] as const;
export type RayOfsmSap = (typeof RAY_OFSM_SAPS)[number];

export const RAY_OFSM_KINDS = ['patch-weld', 'splitter'] as const;
export type RayOfsmKind = (typeof RAY_OFSM_KINDS)[number];

export const RAY_OFSM_CONNECTOR_TYPES = ['SC/APC', 'LC/APC'] as const;
export type RayOfsmConnectorType = (typeof RAY_OFSM_CONNECTOR_TYPES)[number];

export type RayOfsmModuleDef = {
  sapCode: RayOfsmSap;
  id: string;
  nazev: string;
  paletteLabel: string;
  d5TypeLabel: string;
  heightU: 1 | 2 | 3;
  kind: RayOfsmKind;
  connectorCapacity: number;
  weldCapacity: number;
  trayCount: number;
  seatsPerTray: number;
  connectorType: RayOfsmConnectorType | null;
  splitterCount: number;
  splitterRatio: RozvadecSplitterRatio | null;
  /** Assumed when CETIN docs do not pin the adapter family. */
  splitterInputConnectorType: RayOfsmConnectorType | null;
  splitterOutputConnectorType: RayOfsmConnectorType | null;
  /**
   * 325702: training + SAP name say 6×1:8; one v25 table says 8×1:8.
   * Always 6. Never auto-use 8.
   */
  pendingConfirmation: boolean;
};

function ofsm(def: RayOfsmModuleDef): RayOfsmModuleDef {
  return def;
}

export const RAY_OFSM_MODULES: readonly RayOfsmModuleDef[] = [
  ofsm({
    sapCode: '325704',
    id: 'RAY_OFSM_3U',
    nazev: 'RAY-OFSM 3U',
    paletteLabel: 'RAY-OFSM 3U — 144',
    d5TypeLabel: 'RAY-OFSM-3U',
    heightU: 3,
    kind: 'patch-weld',
    connectorCapacity: 144,
    weldCapacity: 144,
    trayCount: 6,
    seatsPerTray: 24,
    connectorType: 'SC/APC',
    splitterCount: 0,
    splitterRatio: null,
    splitterInputConnectorType: null,
    splitterOutputConnectorType: null,
    pendingConfirmation: false,
  }),
  ofsm({
    sapCode: '325713',
    id: 'RAY_OFSM_2U',
    nazev: 'RAY-OFSM 2U',
    paletteLabel: 'RAY-OFSM 2U — 96',
    d5TypeLabel: 'RAY-OFSM-2U',
    heightU: 2,
    kind: 'patch-weld',
    connectorCapacity: 96,
    weldCapacity: 96,
    trayCount: 4,
    seatsPerTray: 24,
    connectorType: 'SC/APC',
    splitterCount: 0,
    splitterRatio: null,
    splitterInputConnectorType: null,
    splitterOutputConnectorType: null,
    pendingConfirmation: false,
  }),
  ofsm({
    sapCode: '325712',
    id: 'RAY_OFSM_1U_SC',
    nazev: 'RAY-OFSM 1U SC/APC',
    paletteLabel: 'RAY-OFSM 1U — 48 SC/APC',
    d5TypeLabel: 'RAY-OFSM-1U',
    heightU: 1,
    kind: 'patch-weld',
    connectorCapacity: 48,
    weldCapacity: 48,
    trayCount: 2,
    seatsPerTray: 24,
    connectorType: 'SC/APC',
    splitterCount: 0,
    splitterRatio: null,
    splitterInputConnectorType: null,
    splitterOutputConnectorType: null,
    pendingConfirmation: false,
  }),
  ofsm({
    sapCode: '326203',
    id: 'RAY_OFSM_1U_LC',
    nazev: 'RAY-OFSM 1U LC/APC',
    paletteLabel: 'RAY-OFSM 1U — 48 LC/APC',
    d5TypeLabel: 'RAY-OFSM-1U',
    heightU: 1,
    kind: 'patch-weld',
    connectorCapacity: 48,
    weldCapacity: 48,
    trayCount: 2,
    seatsPerTray: 24,
    connectorType: 'LC/APC',
    splitterCount: 0,
    splitterRatio: null,
    splitterInputConnectorType: null,
    splitterOutputConnectorType: null,
    pendingConfirmation: false,
  }),
  ofsm({
    sapCode: '325702',
    id: 'RAY_OFSM_1U_SPL_6X18',
    nazev: 'OFSM 1U — 6× splitter 1:8',
    paletteLabel: 'OFSM 1U — 6× splitter 1:8',
    d5TypeLabel: 'RAY-OFSM-1U-SPL-1:8',
    heightU: 1,
    kind: 'splitter',
    connectorCapacity: 0,
    weldCapacity: 0,
    trayCount: 0,
    seatsPerTray: 0,
    connectorType: null,
    splitterCount: 6,
    splitterRatio: '1:8',
    splitterInputConnectorType: 'SC/APC',
    splitterOutputConnectorType: 'LC/APC',
    pendingConfirmation: true,
  }),
  ofsm({
    sapCode: '325703',
    id: 'RAY_OFSM_1U_SPL_3X16',
    nazev: 'OFSM 1U — 3× splitter 1:16',
    paletteLabel: 'OFSM 1U — 3× splitter 1:16',
    d5TypeLabel: 'RAY-OFSM-1U-SPL-1:16',
    heightU: 1,
    kind: 'splitter',
    connectorCapacity: 0,
    weldCapacity: 0,
    trayCount: 0,
    seatsPerTray: 0,
    connectorType: null,
    splitterCount: 3,
    splitterRatio: '1:16',
    splitterInputConnectorType: 'SC/APC',
    splitterOutputConnectorType: 'LC/APC',
    pendingConfirmation: false,
  }),
];

const BY_SAP = new Map(RAY_OFSM_MODULES.map((item) => [item.sapCode, item]));

export function isRayOfsmSap(value: string | null | undefined): value is RayOfsmSap {
  return (RAY_OFSM_SAPS as readonly string[]).includes(String(value ?? '').trim());
}

export function getRayOfsmModule(sapCode: string | null | undefined): RayOfsmModuleDef | undefined {
  return BY_SAP.get(String(sapCode ?? '').trim() as RayOfsmSap);
}

export function hasRozvadecOfsmRack(typeId?: string | null): boolean {
  return String(typeId ?? '').trim() === 'RSU_ODF';
}

export type RayOfsmTrayDef = {
  trayNumber: number;
  place: 'bottom' | 'middle' | 'top';
  firstConnector: number;
  lastConnector: number;
};

/** Tray 1 is bottom (connectors 1–24). Highest tray is top. User cannot add trays. */
export function rayOfsmTraysFor(sapCode: string | null | undefined): RayOfsmTrayDef[] {
  const def = getRayOfsmModule(sapCode);
  if (!def || def.kind !== 'patch-weld' || def.trayCount <= 0) return [];
  const trays: RayOfsmTrayDef[] = [];
  for (let tray = 1; tray <= def.trayCount; tray += 1) {
    const first = (tray - 1) * def.seatsPerTray + 1;
    const last = tray * def.seatsPerTray;
    const place: RayOfsmTrayDef['place'] =
      tray === 1 ? 'bottom' : tray === def.trayCount ? 'top' : 'middle';
    trays.push({ trayNumber: tray, place, firstConnector: first, lastConnector: last });
  }
  return trays;
}

export type RayOfsmConnectorCoord = {
  position: number;
  column: number;
  rowInColumn: number;
  trayNumber: number;
  trayWeldPosition: number;
};

/**
 * RAYCOM: columns left→right, top→bottom in each column.
 * Col 1 = 1–12, col 2 = 13–24, … (3U: 12 columns → 133–144).
 */
export function rayOfsmConnectorCoord(
  sapCode: string | null | undefined,
  position: number,
): RayOfsmConnectorCoord | null {
  const def = getRayOfsmModule(sapCode);
  if (!def || def.kind !== 'patch-weld') return null;
  if (!Number.isInteger(position) || position < 1 || position > def.connectorCapacity) return null;
  const column = Math.ceil(position / RAY_OFSM_COLUMN_SIZE);
  const rowInColumn = ((position - 1) % RAY_OFSM_COLUMN_SIZE) + 1;
  const trayNumber = Math.ceil(position / def.seatsPerTray);
  const trayWeldPosition = ((position - 1) % def.seatsPerTray) + 1;
  return { position, column, rowInColumn, trayNumber, trayWeldPosition };
}

export function rayOfsmConnectorPositions(sapCode: string | null | undefined): number[] {
  const def = getRayOfsmModule(sapCode);
  if (!def || def.kind !== 'patch-weld') return [];
  return Array.from({ length: def.connectorCapacity }, (_, index) => index + 1);
}

export function rayOfsmColumnCount(sapCode: string | null | undefined): number {
  const def = getRayOfsmModule(sapCode);
  if (!def || def.kind !== 'patch-weld') return 0;
  return def.connectorCapacity / RAY_OFSM_COLUMN_SIZE;
}

export function rayOfsmOccupiedUs(startU: number, heightU: number): number[] {
  if (!Number.isInteger(startU) || !Number.isInteger(heightU) || heightU <= 0) return [];
  return Array.from({ length: heightU }, (_, index) => startU + index);
}

export function isValidRayTr3StartU(startU: number, heightU: number): boolean {
  if (!Number.isInteger(startU) || !Number.isInteger(heightU)) return false;
  if (startU < 1 || heightU < 1) return false;
  return startU + heightU - 1 <= RAY_TR3_RACK_HEIGHT_U;
}
